import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ComplianceStatus = "pending" | "compliant" | "flagged";
export type ComplianceCategory = "Structural" | "Electrical" | "Plumbing" | "Zoning";

export interface ComplianceItem {
  id: string;
  category: ComplianceCategory;
  text: string;
  status: ComplianceStatus;
  hint?: string;
}

export const COMPLIANCE_CATEGORIES: ComplianceCategory[] = ["Structural", "Electrical", "Plumbing", "Zoning"];

// Plain language only — no regulation codes inline.
const SEED: ComplianceItem[] = [
  { id: "found-depth", category: "Structural", text: "Your foundation depth meets requirements", status: "compliant" },
  { id: "wall-thick", category: "Structural", text: "Your outside walls are thick enough", status: "compliant" },
  { id: "roof-anchor", category: "Structural", text: "Your roof is tied down well enough for strong wind", status: "flagged", hint: "The roof straps shown on the plan may not be enough for your area. Your engineer can confirm." },
  { id: "lintels", category: "Structural", text: "Door and window openings have proper support above them", status: "pending" },
  { id: "db-board", category: "Electrical", text: "Your electrical board is in a safe, reachable spot", status: "compliant" },
  { id: "earth-leak", category: "Electrical", text: "Your home has protection against electric shock", status: "pending" },
  { id: "bath-sockets", category: "Electrical", text: "Plugs are a safe distance from baths and showers", status: "pending" },
  { id: "geyser-valve", category: "Plumbing", text: "Your hot water tank has a safety release valve", status: "compliant" },
  { id: "drain-fall", category: "Plumbing", text: "Your drains slope enough to flow properly", status: "flagged", hint: "One drain run looks too flat on the plan. A small change in slope will fix it." },
  { id: "vent-pipe", category: "Plumbing", text: "Your sewer pipes are vented to the outside", status: "pending" },
  { id: "boundary", category: "Zoning", text: "Your home sits far enough from the property line", status: "compliant" },
  { id: "coverage", category: "Zoning", text: "Your home doesn't take up more of the plot than allowed", status: "compliant" },
  { id: "height", category: "Zoning", text: "Your home is within the allowed height", status: "pending" },
];

interface ComplianceState {
  items: ComplianceItem[];
  setStatus: (id: string, status: ComplianceStatus) => void;
}

export const useComplianceStore = create<ComplianceState>()(
  persist(
    (set) => ({
      items: SEED,
      setStatus: (id, status) => set((s) => ({ items: s.items.map((i) => (i.id === id ? { ...i, status } : i)) })),
    }),
    { name: "buildease.compliance" },
  ),
);

export const selectCompletion = (items: ComplianceItem[]) => {
  const done = items.filter((i) => i.status === "compliant").length;
  return { done, total: items.length, pct: items.length ? Math.round((done / items.length) * 100) : 0 };
};
